import axios from 'axios'

export type WikiTagLink = {
  label: string
  slug?: string | null
  tag_id?: number | null
  entity_type?: string | null
  entity_id?: number | null
}

export type WikiLinkedEmployee = {
  employee_id: number
  first_name?: string | null
  last_name?: string | null
}

export type AttachmentSummary = {
  attachment_id: number
  url: string
  title?: string | null
  summary?: string | null
  status: string
  created_at: string
  content_type?: string | null
}

export type WikiAttachmentRecord = {
  attachment_id: number
  file_name: string
  url: string
  content_type: string
  display_type: 'image' | 'video' | 'audio' | 'file'
  size: number
  created_at: string
}

export type WikiPageSummary = {
  page_id: number
  slug: string
  title: string
  summary?: string | null
  event_date?: string | null
  updated_at: string
  tags: WikiTagLink[]
}

export type WikiPageDetail = WikiPageSummary & {
  content_html: string
  content_json?: string | null
  linked_employees: WikiLinkedEmployee[]
  attachments: AttachmentSummary[]
  wiki_attachments: WikiAttachmentRecord[]
}

export type WikiPageListResponse = {
  items: WikiPageSummary[]
  total: number
  page: number
  page_size: number
}

export type WikiSearchSuggestion = {
  articles: WikiPageSummary[]
  tags: WikiTagLink[]
  employees: WikiLinkedEmployee[]
}

export type WikiHashtagRef = {
  label: string
  type: string
  slug?: string | null
  entity_id?: number | null
  tag_id?: number | null
}

export type HashtagSuggestion = {
  label: string
  type: string
  slug?: string | null
  entity_id?: number | null
  tag_id?: number | null
  requires_creation?: boolean
}

export type TagRecord = {
  tag_id: number
  name: string
  slug: string
  tag_type: string
  usage_count?: number
}

export type TagUsageResponse = {
  tag: {
    tag_id: number
    name: string
    slug: string
    tag_type: string
  }
  employees: Array<{
    employee_id: number
    first_name?: string | null
    last_name?: string | null
    city?: string | null
    state?: string | null
    country?: string | null
  }>
  wiki_pages: Array<{
    page_id: number
    slug: string
    title: string
    summary?: string | null
    event_date?: string | null
    updated_at: string
  }>
}

type PageQuery = {
  page?: number
  page_size?: number
  search?: string | null
  tag?: string | null
  employee_id?: number | null
}

export function fetchWikiPages(params: PageQuery = {}) {
  return axios
    .get<WikiPageListResponse>('/api/wiki/pages', { params })
    .then((res) => res.data)
}

export function fetchWikiPage(slug: string) {
  return axios.get<WikiPageDetail>(`/api/wiki/pages/${slug}`).then((res) => res.data)
}

export function createWikiPage(payload: {
  title: string
  slug?: string | null
  summary?: string | null
  content_html: string
  content_json?: string | null
  event_date?: string | null
  hashtags?: WikiHashtagRef[]
  linked_employee_ids?: number[]
}) {
  return axios.post<WikiPageDetail>('/api/wiki/pages', payload).then((res) => res.data)
}

export function updateWikiPage(
  slug: string,
  payload: {
    title?: string
    slug?: string
    summary?: string | null
    content_html?: string
    content_json?: string | null
    event_date?: string | null
    hashtags?: WikiHashtagRef[]
    linked_employee_ids?: number[]
  }
) {
  return axios.put<WikiPageDetail>(`/api/wiki/pages/${slug}`, payload).then((res) => res.data)
}

export function uploadWikiAttachment(slug: string, file: File) {
  const payload = new FormData()
  payload.append('file', file)
  return axios
    .post<WikiAttachmentRecord>(`/api/wiki/pages/${slug}/attachments`, payload)
    .then((res) => res.data)
}

export function deleteWikiAttachment(slug: string, attachmentId: number) {
  return axios.delete(`/api/wiki/pages/${slug}/attachments/${attachmentId}`)
}

export function fetchWikiTags(params: { limit?: number; includeEmpty?: boolean } = {}) {
  const { limit = 50, includeEmpty = false } = params
  return axios
    .get<TagRecord[]>('/api/wiki/tags', { params: { limit, include_empty: includeEmpty } })
    .then((res) => res.data)
}

export function updateWikiTags(
  slug: string,
  payload: { tags?: string[]; hashtags?: WikiHashtagRef[] }
) {
  return axios.patch<WikiPageDetail>(`/api/wiki/pages/${slug}/tags`, payload).then((res) => res.data)
}

export function deleteWikiPage(slug: string) {
  return axios.delete<{ message: string }>(`/api/wiki/pages/${slug}`).then((res) => res.data)
}

export function searchWikiSuggestions(query: string) {
  return axios.get<WikiSearchSuggestion>('/api/wiki/search', { params: { q: query } }).then((res) => res.data)
}

export function fetchWikiPagesForEmployee(employeeId: number) {
  return axios.get<WikiPageSummary[]>(`/api/wiki/employees/${employeeId}/pages`).then((res) => res.data)
}

export function fetchHashtagSuggestions(query: string) {
  return axios
    .get<{ results: HashtagSuggestion[] }>('/api/wiki/hashtag-options', { params: { q: query } })
    .then((res) => res.data.results)
}

export function createCustomTag(name: string) {
  return axios.post<TagRecord>('/api/wiki/tags', { name }).then((res) => res.data)
}

export function deleteWikiTag(slug: string) {
  return axios.delete<{ message: string }>(`/api/wiki/tags/${slug}`).then((res) => res.data)
}

export function fetchTagUsage(slug: string) {
  return axios.get<TagUsageResponse>(`/api/wiki/tags/${slug}/usage`).then((res) => res.data)
}
